import type { EntryPointTracking } from '@de/types/types';
import {
  parseCustomDimensionMapping,
  parseProductFieldMapping,
  type CustomDimensionMapping,
  type ProductFieldMapping,
} from './productEventMappingContract';

/**
 * Pull `data.root.props.tracking` out of a Puck-shaped DSL `data`
 * payload. Authors use this block to wire Product fields against a
 * caller-supplied resolve root (`flowContext` on Entry, repeater
 * `item` on EL).
 *
 * Defensive parsing — returns `null` when there's nothing usable so
 * callers can branch on `!== null` without re-checking emptiness:
 *   - Missing root, props, or tracking block → `null`.
 *   - `tracking` not an object → `null` + warn.
 *   - `productFields` not an object → warn and omit that map.
 *   - `customDimensions` not an array → warn and omit that array.
 *   - Per-key `productFields` values go through
 *     {@link parseProductFieldMapping}: legacy strings and
 *     `{ dataPath }` / `{ literal }` are kept; empty, malformed,
 *     both-keys, unknown-key objects, and `{ literal }` on
 *     non-literal wire keys are dropped with a warn.
 *   - Per-entry `customDimensions` go through
 *     {@link parseCustomDimensionMapping}; empty / invalid entries
 *     are dropped with a warn.
 *
 * Returns a tracking object when either sanitized `productFields` or
 * `customDimensions` survives. Dual-shape `productFields` parsing is
 * unchanged.
 */
export function extractTrackingConfig(
  data: unknown,
): EntryPointTracking | null {
  const props = getRootProps(data);
  if (!props || !('tracking' in props)) {
    return null;
  }

  const raw = props.tracking;
  if (!isPlainObject(raw)) {
    // eslint-disable-next-line no-console
    console.warn('[ConfigurableStep] tracking is not an object; skipping.');
    return null;
  }

  const productFields = sanitizeProductFields(raw.productFields);
  const customDimensions = sanitizeCustomDimensions(raw.customDimensions);
  if (!productFields && !customDimensions) return null;

  return {
    ...(productFields ? { productFields } : {}),
    ...(customDimensions ? { customDimensions } : {}),
  };
}

/**
 * Normalize one `productFields` map to dual-shape values the resolver
 * understands. Returns `undefined` when every entry is dropped.
 *
 * @param {unknown} raw - Raw `tracking.productFields` value
 * @returns {Record<string, ProductFieldMapping> | undefined} Sanitized map
 */
function sanitizeProductFields(
  raw: unknown,
): Record<string, ProductFieldMapping> | undefined {
  if (raw === undefined) return undefined;
  if (!isPlainObject(raw)) {
    // eslint-disable-next-line no-console
    console.warn(
      '[ConfigurableStep] tracking.productFields is not an object; skipping.',
    );
    return undefined;
  }

  const out: Record<string, ProductFieldMapping> = {};
  for (const [key, value] of Object.entries(raw)) {
    const parsed = parseProductFieldMapping(key, value);
    if (parsed.kind === 'dataPath') {
      out[key] =
        typeof value === 'string' ? value : { dataPath: parsed.dataPath };
      continue;
    }
    if (parsed.kind === 'literal') {
      out[key] = { literal: parsed.literal };
      continue;
    }

    const reason = parsed.kind === 'invalid' ? parsed.reason : parsed.kind;
    // eslint-disable-next-line no-console
    console.warn(
      `[ConfigurableStep] tracking.productFields["${key}"] is invalid (${reason}); skipping.`,
    );
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Normalize sibling `tracking.customDimensions` to parseable mappings.
 * Does not resolve paths — emit sites resolve against the live root.
 *
 * @param {unknown} raw - Raw `tracking.customDimensions` value
 * @returns {CustomDimensionMapping[] | undefined} Sanitized mappings
 */
function sanitizeCustomDimensions(
  raw: unknown,
): CustomDimensionMapping[] | undefined {
  if (raw === undefined) return undefined;
  if (!Array.isArray(raw)) {
    // eslint-disable-next-line no-console
    console.warn(
      '[ConfigurableStep] tracking.customDimensions is not an array; skipping.',
    );
    return undefined;
  }

  const out: CustomDimensionMapping[] = [];
  for (const value of raw) {
    const parsed = parseCustomDimensionMapping(value);
    if (parsed.kind === 'dataPath') {
      out.push({ key: parsed.key, dataPath: parsed.dataPath });
      continue;
    }
    if (parsed.kind === 'literal') {
      out.push({ key: parsed.key, literal: parsed.literal });
      continue;
    }

    const reason = parsed.kind === 'invalid' ? parsed.reason : parsed.kind;
    // eslint-disable-next-line no-console
    console.warn(
      `[ConfigurableStep] tracking.customDimensions entry is invalid (${reason}); skipping.`,
    );
  }
  return out.length > 0 ? out : undefined;
}

function getRootProps(data: unknown): Record<string, unknown> | undefined {
  if (!isPlainObject(data)) return undefined;
  const root = data.root;
  if (!isPlainObject(root)) return undefined;
  const props = root.props;
  if (!isPlainObject(props)) return undefined;
  return props;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
